import {useCallback, ReactNode} from 'react';

import {defineMessages, FormattedMessage, MessageDescriptor} from 'react-intl';

import {css, Icon, Action, Tooltip, createView} from '@sail/ui';
import {cancel, chevronDown, chevronUp} from '@sail/icons/react/Icon';

import {useAnalytics} from '../AnalyticsProvider';
import {AssistantHeader} from './AssistantHeader';

const messages = defineMessages({
  minimizeTooltip: {
    id: 'conversations_ui.header.minimizeTooltip',
    description: 'Label for the minimize botton',
    defaultMessage: 'Minimize',
  },
  closeTooltip: {
    id: 'conversations_ui.header.closeTooltip',
    description: 'Label for the close botton',
    defaultMessage: 'Close',
  },
});

const buttonStyles = css({
  display: 'inline-flex',
  borderRadius: 'rounded',
  padding: 'small',
  ':hover': {
    backgroundColor: 'offset',
  },
});

type Props = {
  title?: MessageDescriptor;
  height?: number | string;
  badge?: ReactNode;
  onClose: () => void;
  onMinimize?: () => void;
  onOpen?: () => void;
  visibility?: 'open' | 'minimized' | 'closed';
  headerActions?: ReactNode;
  subHeader?: ReactNode;
};

export const AssistantWidgetHeader = createView(
  ({
    title,
    height = 52,
    badge,
    visibility = 'open',
    onMinimize,
    onOpen,
    onClose,
    headerActions,
    subHeader,
    ...props
  }: Props) => {
    const analytics = useAnalytics();

    const minimized = visibility === 'minimized';

    const handleMinimize = useCallback(() => {
      if (minimized) {
        onOpen?.();
      } else {
        onMinimize?.();
      }
      analytics.action('stripe_assistant_header_minimize_click');
    }, [minimized, onOpen, onMinimize, analytics]);

    const handleClose = useCallback(() => {
      onClose();
      analytics.action('stripe_assistant_header_close_click');
    }, [analytics, onClose]);

    const allHeaderActions = (
      <>
        {headerActions}
        <Tooltip
          type="label"
          delay={600}
          trigger={
            <Action
              css={{padding: 'small'}}
              onPress={handleMinimize}
              uses={[buttonStyles]}
            >
              <Icon
                data-testid="assistant-header-minimize-button"
                icon={minimized ? chevronUp : chevronDown}
                size="xsmall"
              />
            </Action>
          }
        >
          <FormattedMessage {...messages.minimizeTooltip} />
        </Tooltip>

        <Tooltip
          type="label"
          delay={600}
          trigger={
            <Action
              data-testid="conversations-widget-header-close-button"
              onPress={handleClose}
              uses={[buttonStyles]}
            >
              <Icon icon={cancel} size="xsmall" />
            </Action>
          }
        >
          <FormattedMessage {...messages.closeTooltip} />
        </Tooltip>
      </>
    );

    return (
      <AssistantHeader
        title={title}
        height={height}
        badge={badge}
        headerActions={allHeaderActions}
        subHeader={subHeader}
        {...props}
      />
    );
  },
);
